import { useAsync } from "hooks";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

require('./PlayAudioButton.css');

export type Props = {
    className?: string;
    filename: string | null;
    disabled?: boolean;
};

export const PlayAudioButton = ({ className, filename, disabled = false }: Props) => {
    const { t } = useTranslation();
    const { isMounted } = useAsync();
    const [ playing, setPlaying ] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const handleAudioEnded = useCallback(() => {
        audioRef.current = null;
        if (!isMounted()) return;
        setPlaying(false);
    }, [ setPlaying, audioRef ]);

    const handleClickPlay = useCallback(() => {
        setPlaying(true);
        void (async () => {
            try {
                if (!audioRef.current) {
                    audioRef.current = new Audio(filename!);
                    audioRef.current.addEventListener('ended', handleAudioEnded);
                    audioRef.current.addEventListener('error', handleAudioEnded);
                }
                await audioRef.current.play();
            }
            catch (err) {
                console.error(`Failed to play audio file: ${filename}`, err);
            }
        })();
    }, [ setPlaying, audioRef, filename, handleAudioEnded ]);

    const handleClickStop = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        try {
            audio.pause();
            setPlaying(false);
        }
        catch (err) {
            // Swallow the error
        }
    }, [ audioRef ]);

    return (
        <button
            type="button"
            className={ `play-audio-button ui tiny icon basic button ${className || ''}` }
            disabled={ disabled || !filename }
            onClick={ playing ? handleClickStop : handleClickPlay }
            title={ t(playing ? 'COMMAND.STOP' : 'COMMAND.PLAY') }
        >
            <i className={ `${playing ? 'stop' : 'play'} icon` }/>
        </button>
    );
};
